import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Body, Confetti, Headline, HoverButton, HoverCard, MiniHeroIcon } from 'src/components';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import { Margin } from 'src/constants';
import styled from 'styled-components/native';
import { ConfirmationSection, FormScrollView } from 'src/module/FormKit/components';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, ProgressBar } from 'react-native-paper';
import RichTextRenderer from 'src/components/PracticeTextRenderer';
import { RefillFormState } from '../model';
import { REFILL_CONFIRMATION } from '../helpers/productConfirmation';
import { LayoutAnimation } from 'react-native';
import { AlertCircle, Prescriptions } from 'src/components/kit/ABIcons';

const MAX_ERRORED_SUBMISSIONS = 3;

const RefillStatus: React.FC<RefillFormState> = (props) => {
  const {
    confirmationText,
    isSubmitting: _isSubmitting,
    submitCount,
    isSubmitError,
    handleSubmit
  } = props;
  const { colors } = useAppTheme();
  const { t } = useTranslation('requests');

  const [isSubmitting, setIsSubmitting] = useState(_isSubmitting);
  const [isError, setIsError] = useState(isSubmitError);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsSubmitting(_isSubmitting);
      setIsError(isSubmitError);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [_isSubmitting, isSubmitError]);
  useLayoutEffect(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
  }, [isSubmitting]);

  return (
    <FormScrollView>
      {submitCount > 0 && !isSubmitting && !isError && <StyledConfetti />}
      <StyledCard>
        <MiniHeroIcon
          icon={(props) =>
            isSubmitting ? (
              <ActivityIndicator {...props} color={props.color as string} />
            ) : isError ? (
              <AlertCircle {...props} color={props.color as string} />
            ) : (
              <Prescriptions {...props} color={props.color} />
            )
          }
          color={isSubmitting ? colors.warn : isError ? colors.error : colors.primary}
        />
        {isSubmitting && <CenteredText as={Headline}>{t(`refillRequest.submitting`)}</CenteredText>}
        {!isSubmitting && !isError && (
          <CenteredText as={Headline}>{t(`refillRequest.success`)}</CenteredText>
        )}
        {isError && <CenteredText as={Headline}>{t(`refillRequest.error`)}</CenteredText>}
        <ProgressBar
          indeterminate={isSubmitting}
          color={isSubmitting ? colors.warn : isError ? colors.error : colors.primary}
          progress={1}
        />

        {!isSubmitting && !isError && (
          <RichTextRenderer
            baseStyle={{ color: colors.onSurface }}
            fallback={<CenteredText>{t('refillRequest.successMessage')}</CenteredText>}
          >
            {confirmationText}
          </RichTextRenderer>
        )}
        {isError && submitCount < MAX_ERRORED_SUBMISSIONS && (
          <>
            <CenteredText>{t('refillRequest.submitErrorRetry')}</CenteredText>
            <HoverButton onPress={handleSubmit} disabled={isSubmitting} mode='contained'>
              {'Resubmit'}
            </HoverButton>
          </>
        )}
        {isError && submitCount >= MAX_ERRORED_SUBMISSIONS && (
          <CenteredText>{t('refillRequest.submitErrorMax')}</CenteredText>
        )}
      </StyledCard>
      <ConfirmationSection disableEditing {...props} section={REFILL_CONFIRMATION.additionalInfo} />
    </FormScrollView>
  );
};

export default RefillStatus;

const StyledCard = styled(HoverCard)`
  padding: ${Margin.ExtraLarge}px;
  gap: ${Margin.Large}px;
`;

const CenteredText = styled(Body)`
  text-align: center;
  color: ${({ theme }) => theme.colors.onSurface};
`;

const StyledConfetti = styled(Confetti).attrs({
  preserveAspectRatio: 'xMidYMid slice'
})`
  position: absolute;
  width: 100%;
  bottom: 0px;
  left: 0px;
`;
